import { Experiment } from '@amplitude/experiment-js-client'
import { useLocalStorage } from '@mantine/hooks'
import debug from 'debug'
import { memoize } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createMutator, createSWR } from 'src/services/swr'
import { config } from './config'
import { attemptParseJSON } from './index'

const log = debug('pixai:experiment')

/** Name: web_first-deployment */
let experiment: ReturnType<(typeof Experiment)['initialize']>

async function initExperimentClient() {
  experiment = Experiment.initializeWithAmplitudeAnalytics(
    config.amplitudeExperimentDeploymentKey,
    {
      serverUrl: config.amplitudeExperimentsServerUrl,
      flagsServerUrl: config.amplitudeExperimentsFlagsServerUrl,
      automaticFetchOnAmplitudeIdentityChange: true,
      automaticExposureTracking: false,
      instanceName: 'exp-new',
    },
  )
  await experiment.start()
  return experiment
}

export async function getExperiment() {
  experiment ||= await initExperimentClient()
  return experiment
}

export async function getExperimentParam<T>(experimentName: string) {
  if (!experiment) {
    experiment = await initExperimentClient()
  } else {
    // Refetch for most up-to-date variants
    await experiment.fetch()
  }
  const variant = experiment.variant(experimentName)
  return variant.value as T
}

export async function getExperimentParamWithLocalStorage<T>(
  experimentName: string,
  defaultValue?: T,
): Promise<T | undefined> {
  const localValue = localStorage.getItem(`exp:${experimentName}`)
  // If local value exists, skip requesting remote value
  if (localValue) return JSON.parse(localValue)

  const variantData = await getExperimentParam<T>(experimentName)
  return variantData ?? defaultValue ?? undefined
}

const _logExperimentActivated = (experimentName: string, remoteValue: string) =>
  log(
    `Experiment ${JSON.stringify(experimentName)} ${JSON.stringify(remoteValue)} variant activated.` +
      ' New variant will be enabled once next page refresh.',
  )

const _logExperimentChanged = (
  experimentName: string,
  localValue: string,
  remoteValue: string,
) =>
  log(
    `Experiment "${experimentName}" variant changed from "${localValue}" to "${remoteValue}".`,
  )

const _logExperimentFetchFailed = (experimentName: string, error: Error) =>
  console.error(
    `Failed to fetch parameter for experiment ${JSON.stringify(experimentName)} with failure:`,
    error,
  )

/**
 * Synchronously returns the cached experiment variant from localStorage.
 * Fetches the latest value from server in the background and updates localStorage.
 * New/updated variants take effect on the next page refresh.
 *
 * **The DEBUG PANEL does NOT affect this function;**
 * please use the Amplitude dashboard for debugging instead.
 *
 * @returns Cached variant string, or `undefined` if no valid cached value exists.
 */
export const getExperimentParamSync = memoize(
  <T extends string>(experimentName: string): T | undefined => {
    // SSR-safe: localStorage is undefined on the server. Modules that call
    // this at load time (e.g. migratedRouteModules) ship in both modes.
    if (typeof window === 'undefined') return undefined
    const storageKey = `exp:${experimentName}`

    const localValue: T | undefined = attemptParseJSON(
      localStorage.getItem(storageKey),
    )

    /**
     * Statements below here can be executed once time for each session (as long as the page is not refreshed).
     * - `fetchRemote`: Fetch latest value from server in the background, update localStorage and log if necessary.
     * - `exposure`: Report experiment exposure, only once per session.
     */

    const fetchRemote = async () => {
      // Fetch latest value from server in the background.
      const remoteValue = await getExperimentParam<T | undefined>(
        experimentName,
      )
      if (typeof remoteValue === 'string' && remoteValue !== '') {
        localStorage.setItem(storageKey, JSON.stringify(remoteValue))
        if (log.enabled) {
          if (!(typeof localValue === 'string' && localValue !== '')) {
            _logExperimentActivated(experimentName, remoteValue)
          } else if (localValue !== remoteValue) {
            _logExperimentChanged(experimentName, localValue, remoteValue)
          }
        }
      } else {
        localStorage.removeItem(storageKey)
      }
    }

    fetchRemote().catch(err => _logExperimentFetchFailed(experimentName, err))

    if (typeof localValue === 'string' && localValue !== '') {
      getExperiment().then(experiment => {
        experiment.exposure(experimentName)
      })
      return localValue
    } else {
      return undefined
    }
  },
)

const _useExperimentParam = createSWR(getExperimentParam)
const _useExperimentParamWithoutRevalidate = createSWR(getExperimentParam, {
  revalidateOnFocus: false,
})
export const mutateExperimentParam = createMutator(getExperimentParam)

function useExperimentExposure(
  experimentName: string,
  value: unknown,
  enableAutoExpose?: boolean,
) {
  const autoExpose = enableAutoExpose ?? true
  const exposedRef = useRef(false)

  useEffect(() => {
    exposedRef.current = false
  }, [experimentName])

  useEffect(() => {
    if (!autoExpose) return
    if (exposedRef.current) return
    // 'off' is Amplitude Experiment's sentinel value for users not targeted by the experiment.
    // experiment.all() returns { value: 'off' } for non-participants, unlike experiment.variant()
    // which returns undefined. We must not fire an exposure for these users.
    if (value === null || value === undefined || value === 'off') return
    try {
      experiment?.exposure(experimentName)
      exposedRef.current = true
    } catch (err) {
      console.error('Error reporting experiment exposure:', err)
    }
  }, [autoExpose, experimentName, value])
}

/**
 * @param experimentName
 * @param defaultValue
 * @returns Returns `undefined` if experiment isn't enabled or doesn't exist.
 * @returns Returns `null` if the user hasn't been bucketed into a variant.
 */
export function useExperimentParam<T = string | undefined | null>(
  experimentName: string,
  defaultValue?: T,
  opt?: {
    /**
     * Only return value from remote
     * Ignore localStorage ones and defaultValue
     */
    remoteOnly?: boolean
    autoExpose?: boolean
  },
) {
  const fallbackDefault = defaultValue ?? undefined
  const { data: variantData } = _useExperimentParam(experimentName)
  const [localValue] = useLocalStorage<T>({ key: `exp:${experimentName}` })
  useExperimentExposure(experimentName, variantData, opt?.autoExpose)

  if (opt?.remoteOnly) {
    return variantData as T
  }

  return localValue ?? (variantData as T) ?? fallbackDefault
}

export function experimentLogout() {
  // NOTE: experiment will be undefined if not initialized properly
  return experiment?.clear()
}

/**
 * Manually report an exposure event for the given experiment.
 *
 * Note: This utility does not deduplicate calls. If you call it multiple times,
 * multiple exposures may be reported. Prefer the hook-managed enableExposure()
 * (via useExperimentVariant).
 *
 * @param experimentName experiment key used in Amplitude Experiment
 */
export function reportExperimentExposure(experimentName: string) {
  experiment?.exposure(experimentName)
}

/**  Alternate version of `useExperimentParam` that
 * - won't revalidate on refocus
 * - returns local storage override on first render
 * - returns a loading state
 */
export function useExperimentParamValue<T = string>(
  experimentName: string,
  defaultValue?: NoInfer<T>,
  opt?: { autoExpose?: boolean },
) {
  const { data: variantData, isLoading } =
    _useExperimentParamWithoutRevalidate(experimentName)
  const [localValue] = useLocalStorage<T>({
    key: `exp:${experimentName}`,
    getInitialValueInEffect: false,
  })
  useExperimentExposure(experimentName, variantData, opt?.autoExpose)
  return {
    value: localValue ?? (variantData as T | undefined) ?? defaultValue,
    loading: isLoading,
  }
}

/**
 * Shared variants cache: get variant instantly from cache, no need to handle loading
 */
export async function getAllVariants() {
  if (!experiment) {
    experiment = await initExperimentClient()
  }
  await experiment.fetch()
  return experiment.all() as Record<string, { value?: string | null }>
}

export const useAllVariants = createSWR(getAllVariants, {
  revalidateOnFocus: false,
})

export const mutateAllVariants = createMutator(getAllVariants)

/**
 * Different from useExperimentParam, this will get variant instantly from cache, no need to handle loading for most cases
 * enableExposure is idempotent; repeated calls have no additional effect beyond the first exposure.
 */
export function useExperimentVariant<T = string | undefined | null>(
  experimentName: string,
  defaultValue?: T,
  opt?: { autoExpose?: boolean; remoteOnly?: boolean },
): { value: T; enableExposure: () => void } {
  const { data: variants } = useAllVariants()
  const v = variants?.[experimentName]
  const [localValue] = useLocalStorage<T>({ key: `exp:${experimentName}` })
  const [exposureEnabled, setExposureEnabled] = useState<boolean>(
    opt?.autoExpose ?? true,
  )
  useExperimentExposure(experimentName, v?.value, exposureEnabled)

  const enableExposure = useCallback(() => {
    setExposureEnabled(true)
  }, [])

  const remoteValue = v?.value as T | undefined
  if (opt?.remoteOnly) {
    return { value: remoteValue as T, enableExposure }
  }

  const fallbackDefault = defaultValue ?? undefined

  const value = (localValue ?? remoteValue ?? fallbackDefault) as T

  return { value, enableExposure }
}

/**
 * Bootstrapper: call useAllVariants once at app start to warm the cache.
 */
export function ExperimentVariantsBootstrap() {
  useAllVariants()
  return null
}
